import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress
} from '@mui/material';
import { customerService } from '../services/customerService';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { customerPaymentService } from '../services/customerPaymentService';
import { orderService } from '../services/orderService';
import { formatCurrency } from './CurrencyTextField';
import { format } from 'date-fns';

const PAYMENT_METHODS = ['Check', 'ACH', 'Cash', 'Wire', 'Credit Card'];

const RecordPaymentDialog = ({ open, onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrderId, setWorkOrderId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [openInvoices, setOpenInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setAmount('');
      setReferenceNumber('');
      setNotes('');
      setAllocations({});
      setCustomerId('');
      setWorkOrderId('');
      setWorkOrders([]);
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentMethod('Check');
      loadCustomers();
    }
  }, [open]);

  useEffect(() => {
    if (open && customerId) {
      setWorkOrderId('');
      loadWorkOrdersForCustomer();
      loadOpenInvoices();
    } else {
      setWorkOrders([]);
      setWorkOrderId('');
      setOpenInvoices([]);
      setAllocations({});
    }
  }, [open, customerId]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerService.getAll({ limit: 500 });
      setCustomers(res?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrdersForCustomer = async () => {
    try {
      const res = await orderService.getAll({
        customerId: parseInt(customerId, 10),
        limit: 300,
        include_superseded: '0'
      });
      setWorkOrders(res?.data ?? []);
    } catch {
      setWorkOrders([]);
    }
  };

  const loadOpenInvoices = async () => {
    try {
      const res = await customerInvoiceService.getOpenByCustomerId(customerId);
      setOpenInvoices(res?.data ?? []);
      setAllocations({});
    } catch (err) {
      setOpenInvoices([]);
      setError(err.response?.data?.message || 'Failed to load open invoices');
    }
  };

  const totalAllocated = () => {
    return Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  };

  const setAllocation = (invoiceId, val) => {
    const num = parseFloat(val);
    setAllocations((prev) => ({
      ...prev,
      [invoiceId]: isNaN(num) ? '' : num
    }));
  };

  const handleSubmit = async () => {
    setError('');
    const amt = parseFloat(amount);
    if (!customerId || !amt || amt <= 0) {
      setError('Customer and a positive amount are required.');
      return;
    }
    const woId = parseInt(workOrderId, 10);
    if (!Number.isFinite(woId)) {
      setError('Work order is required.');
      return;
    }

    const allocList = Object.entries(allocations)
      .filter(([, v]) => v && parseFloat(v) > 0)
      .map(([invId, v]) => ({ invoice_id: parseInt(invId, 10), amount: parseFloat(v) }));

    const totalAlloc = allocList.reduce((s, a) => s + a.amount, 0);
    if (allocList.length > 0 && Math.abs(totalAlloc - amt) > 0.01) {
      setError(`Total applied (${formatCurrency(totalAlloc)}) must equal payment amount (${formatCurrency(amt)}).`);
      return;
    }

    try {
      setSaving(true);
      await customerPaymentService.create({
        customer_id: parseInt(customerId, 10),
        work_order_id: woId,
        payment_date: paymentDate,
        amount: amt,
        payment_method: paymentMethod,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
        allocations: allocList
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const customerName = (c) =>
    c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`;
  const formatDate = (d) => (d ? format(new Date(d), 'MM/dd/yyyy') : '—');

  const orderLabel = (o) =>
    (o.identifier != null && String(o.identifier).trim()) ||
    (o.work_order_number != null && String(o.work_order_number)) ||
    (o.id != null ? `#${o.id}` : '');

  const selectedWoIdStr = workOrderId ? String(workOrderId) : '';
  const openInvoicesForOrder = openInvoices.filter(
    (inv) => inv.work_order_id != null && String(inv.work_order_id) === selectedWoIdStr
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Payment</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              label="Customer"
              disabled={loading}
            >
              <MenuItem value="">Select customer</MenuItem>
              {(customers || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {customerName(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" required disabled={!customerId}>
            <InputLabel>Work order</InputLabel>
            <Select
              value={workOrderId}
              onChange={(e) => {
                setWorkOrderId(e.target.value);
                setAllocations({});
              }}
              label="Work order"
            >
              <MenuItem value="">Select order</MenuItem>
              {(workOrders || []).map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  {orderLabel(o)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              label="Amount"
              type="number"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label="Method"
              >
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Reference (e.g. check #)"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Notes"
            multiline
            rows={1}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {customerId && workOrderId && openInvoicesForOrder.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Apply to invoices (this order)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Balance</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Apply</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openInvoicesForOrder.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoice_number}</TableCell>
                      <TableCell>{formatDate(inv.due_date)}</TableCell>
                      <TableCell align="right">{formatCurrency(inv.balance_due, '0')}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={allocations[inv.id] ?? ''}
                          onChange={(e) => setAllocation(inv.id, e.target.value)}
                          inputProps={{ min: 0, max: inv.balance_due, step: 0.01 }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Total applied: {formatCurrency(totalAllocated())} — Payment amount: {formatCurrency(amount || 0)}
              </Typography>
            </Box>
          )}

          {customerId && workOrderId && openInvoicesForOrder.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No open invoices for this order. You can still record an on-account payment for this order.
            </Typography>
          )}

          {customerId && !workOrderId && (
            <Typography variant="body2" color="text.secondary">
              Select a work order to continue.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
